import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { Pin } from '@watersport/ui-react-icons'
import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react'

import { useSearch } from 'contexts/SearchProvider'
import { getMyLocation } from 'features/common/helpers/getMyLocation'
import { getPlaceName } from 'features/common/helpers/getPlaceName'
import { SearchScope } from 'features/common/types'
import AutoCompletePlace from 'features/locationMap/components/AutoCompletePlace'
import CircleOnMap from 'features/locationMap/components/CircleOnMap'
import ConfirmLocationButton from 'features/locationMap/components/ConfirmLocationButton'
import DistanceSelector from 'features/locationMap/components/DistanceSelector'
import Toast from 'features/locationMap/components/Toast'
import { useMapEvents } from 'features/locationMap/hooks/useMapEvents'

export interface SelectMapViewProps {
  scope: SearchScope | undefined
  setScope: (scope: SearchScope | undefined) => void
  onClose: () => void
  onSearch: () => void
  onChangeViewMode: (mode: 'select' | 'drawing') => void
}

export interface SelectMapViewInterface {
  setInitialScope: (scope: SearchScope) => void
  onClickMyLocation: () => void
}

const SelectMapView: React.ForwardRefRenderFunction<SelectMapViewInterface, SelectMapViewProps> = (
  { scope, setScope, onClose, onSearch, onChangeViewMode },
  ref,
) => {
  const { myLocation } = useSearch()
  const [center, setCenter] = useState<google.maps.LatLngLiteral | undefined>(undefined)
  const [distance, setDistance] = useState<number | undefined>(10)
  const [placeName, setPlaceName] = useState<string>('')
  const [showTopBottom, setShowTopBottom] = useState<boolean>(true)

  const onCenterChanged = useCallback((newCenter: google.maps.LatLng | undefined) => {
    if (newCenter) {
      setCenter({
        lat: newCenter.lat(),
        lng: newCenter.lng(),
      })
    }
  }, [])

  const onDragStart = useCallback(() => {
    if (center?.lat !== scope?.center?.lat || center?.lng !== scope?.center?.lng) {
      setShowTopBottom(false)
    }
  }, [center, scope?.center])

  const updatePlace = useCallback(
    async (c: google.maps.LatLngLiteral) => {
      const name = await getPlaceName(c)
      setPlaceName(name)
      setScope({ center, distance, placeName: name })
    },
    [center, distance, setScope],
  )

  const onPlaceSelected = useCallback(
    (place: google.maps.LatLngLiteral | undefined, name: string | undefined) => {
      setCenter(place)
      setDistance(5)
      setPlaceName(name || '')
      setScope({ center: place, distance, placeName: name ?? '' })
    },
    [distance, setScope],
  )

  const onClickMyLocation = useCallback(async () => {
    if (myLocation) {
      updatePlace(myLocation).then()
    }
  }, [myLocation, updatePlace])

  const onClickConfirm = useCallback(async () => {
    setShowTopBottom(true)
    if (center) {
      updatePlace(center).then()
    }
  }, [center, updatePlace])

  const onClickCancel = useCallback(() => {
    setCenter(scope?.center)
    setDistance(scope?.distance)
    setPlaceName(scope?.placeName ?? '')
    setShowTopBottom(true)
  }, [scope?.center, scope?.distance, scope?.placeName])

  const onChangeDistance = useCallback(
    (d: number | undefined) => {
      setDistance(d ?? 100)
      if (scope) setScope({ ...scope, distance: d ?? 100 })
    },
    [scope, setScope],
  )

  useEffect(() => {
    if (myLocation) {
      setCenter(myLocation)
      getPlaceName(myLocation).then(name => {
        setPlaceName(name)
        setScope({ center: myLocation, distance: 5, placeName: name })
      })
    } else {
      getMyLocation().then(c => {
        setCenter(c.coordinate)
        setPlaceName(c.region ?? '')
        setScope({ center: c.coordinate, distance: 5, placeName: c.region ?? '' })
      })
    }
  }, [myLocation, setScope])

  useMapEvents({ onDragStart, onZoomChanged: onDragStart, onCenterChanged })

  const autoCompletePlace = React.useRef<React.ElementRef<typeof AutoCompletePlace>>(null)

  useImperativeHandle(
    ref,
    () => ({
      setInitialScope: (s: SearchScope) => {
        autoCompletePlace.current?.setInitialScope(s)
      },
      onClickMyLocation: () => {
        onClickMyLocation().then()
      },
    }),
    [onClickMyLocation],
  )

  return (
    <>
      {center && (
        <AdvancedMarker position={center}>
          <Pin width={31} height={44} />
        </AdvancedMarker>
      )}
      {!showTopBottom && (
        <div
          className="absolute bottom-4 left-0 right-0 w-full flex flex-col gap-4"
          data-testid="confirm-location-buttons"
        >
          <ConfirmLocationButton onConfirm={onClickConfirm} onCancel={onClickCancel} />
        </div>
      )}
      <DistanceSelector
        show={showTopBottom}
        distance={distance}
        onChangeDistance={onChangeDistance}
        onSearch={onSearch}
        onChangeViewMode={onChangeViewMode}
      />
      <AutoCompletePlace
        show={showTopBottom}
        placeName={placeName}
        center={center}
        onClose={onClose}
        onPlaceSelected={onPlaceSelected}
        onClickMyLocation={onClickMyLocation}
        onClearSearch={() => setPlaceName('')}
        ref={autoCompletePlace}
      />
      <CircleOnMap center={center} distance={distance} show={showTopBottom} />
      <Toast message={'Select area and radius mode'} />
    </>
  )
}

export default React.forwardRef(SelectMapView)
